import type { ReactNode } from 'react'
import type { AchievementType } from '../api/types'
import type { BannerCard, PartnerLogo, TestimonialCard } from '../helpers/mapContent'
import { ACHIEVEMENT_TYPE } from './api'

export const HERO_CAROUSEL = {
  autoplayMs: 6500,
  /** Matches CSS mobile hero breakpoint — use mobile banner art below this. */
  mobileMaxWidthPx: 720,
} as const

export const STOREFRONT_HOME = {
  inRangeTitle: "Make Someone's Day Bloom",
  inRangeSubtitle: 'Flowers and gifts ready to order',
  standardTitle: 'Flower for Every Occasions...',
  standardSubtitle: 'More flowers, plants, and gifts',
  standardEmpty: 'Nothing to show here yet.',
  plantsTitle: 'Bring Home Something That Grows',
  plantsSubtitle: 'Greenery for home and gifting',
} as const

/** Home product grids show this many rows. Column counts match home-page.scss. */
export const HOME_CATALOG_ROWS = 4

export const HOME_CATALOG_COLUMNS = {
  mobile: 2,
  tablet: 4,
  desktop: 5,
  tabletMinPx: 720,
  desktopMinPx: 1100,
} as const

export const FLOWERS_META = {
  title: 'Flowers',
  subtitle: 'Fresh bouquets for every moment',
  taxonSlug: 'flowers',
  seeAllLabel: 'See all flowers',
  seeAllTo: '/shop/flowers',
} as const

export const INDOOR_PLANTS_META = {
  title: 'Indoor Plants',
  subtitle: 'Greenery that thrives indoors',
  taxonSlug: 'indoor-plants',
  limit: 4,
  seeAllLabel: 'See all plants',
  seeAllTo: '/shop/indoor-plants',
} as const

export const ACHIEVEMENT_CAROUSELS = [
  {
    type: ACHIEVEMENT_TYPE.recognized,
    title: 'Recognized & Supported by Government Initiatives',
  },
  {
    type: ACHIEVEMENT_TYPE.partner,
    title: 'Our Partners',
  },
] as const

export const PARTNERS_MARQUEE = {
  /** Minimum logos in one rail group before duplicating for the loop. */
  minItems: 8,
  durationSec: 22,
  fallbackItemWidthPx: 240,
} as const

export const TESTIMONIALS_META = {
  title: 'What Our Customers Say',
  subtitle: 'Real stories from real deliveries',
  loadingAria: 'Loading testimonials',
} as const

export const TESTIMONIALS_CAROUSEL = {
  autoplayMs: 5500,
  prevAria: 'Previous testimonial',
  nextAria: 'Next testimonial',
  dotsAria: 'Customer testimonials',
  ratingMax: 5,
  visibleMobile: 1,
  visibleTablet: 2,
  visibleDesktop: 3,
  tabletMinPx: 720,
  desktopMinPx: 1100,
  slideRole: 'slide',
  dotAria: (index: number) => `Show testimonial ${index + 1}`,
  slideAria: (index: number, total: number) => `Testimonial ${index + 1} of ${total}`,
  ratingAria: (rating: number, max: number) => `${rating} out of ${max} stars`,
} as const

export const TESTIMONIALS_KEYBOARD = {
  prev: 'ArrowLeft',
  next: 'ArrowRight',
} as const

export const BLOG_TEASER_META = {
  title: 'The Maaliondoor Journal',
  subtitle: 'Plant care tips, gifting ideas, and seasonal inspiration',
  seeAllLabel: 'Read all articles',
  seeAllTo: '/blog',
} as const

export const BLOG_LIST_META = {
  title: 'The Maaliondoor Journal',
  subtitle: 'Plant care, gifting guides, and seasonal inspiration from our florists',
} as const

export const BLOG_TEASER_LIMIT = 3

export type HomePromoBannersProps = {
  banners: BannerCard[]
}

export type BannerCtaLinkProps = {
  label: string
  url: string
  external: boolean
  className: string
  children?: ReactNode
}

export type PartnersMarqueeProps = {
  title: string
  logos: PartnerLogo[]
}

export type AchievementCarouselSection = {
  type: AchievementType
  title: string
  logos: PartnerLogo[]
}

export type PartnerLogoMarkProps = {
  logo: PartnerLogo
  decorative?: boolean
}

export type TestimonialsCarouselProps = {
  testimonials: TestimonialCard[]
}
